/**
 * Reopen & Fix Week wizard (corrections Phase 3b).
 *
 * A guided flow over EXISTING machinery for a POSTED batch — no new
 * correction primitives:
 *   1. Reopen  — preview per-party balance deltas, then unpost (PIN).
 *   2. Fix     — re-point wrong raw row(s) at the right account via
 *                PATCH /api/raw-rows/:id/match (PIN; recomputes settlements),
 *                or hop out to edit points on the batch page.
 *   3. Repost  — POST /api/batches/:id/post re-creates the entries with the
 *                corrected values.
 *
 * Everything rides the byte-exact post/unpost round-trip; nothing here
 * touches money directly.
 */
import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, ApiError, splitBySeverity } from '../api';
import type {
  Account,
  BatchTotals,
  PostingImpact,
  RawRowReclassifyResult,
  RawUplineRow,
  VerifyBlocker,
  WeeklyBatch,
} from '../api';
import { Button, ErrorBanner, Select, Spinner, TextArea } from '../ui/primitives';
import { Modal } from '../ui/Modal';
import { usePinGate } from '../auth/pinGate';

/** Stored decimal-multiplier rate → UI percent string (matches RateHistory). */
export function formatRatePercent(rate: string | null): string {
  if (rate === null) return 'Self';
  const n = Number(rate);
  return Number.isFinite(n) ? `${+(n * 100).toFixed(4)}%` : rate;
}

type Step = 'reopen' | 'fix' | 'repost' | 'done';

export interface ReopenFixWizardProps {
  batch: WeeklyBatch;
  onClose: () => void;
}

export function ReopenFixWizard({ batch, onClose }: ReopenFixWizardProps) {
  const qc = useQueryClient();
  const { withPin } = usePinGate();
  // A batch that is already back in draft/verified skips straight to Fix.
  const [step, setStep] = useState<Step>(batch.state === 'archived' ? 'reopen' : 'fix');
  const [error, setError] = useState<string | null>(null);
  const [entriesCreated, setEntriesCreated] = useState<number | null>(null);

  const invalidateBatch = () =>
    void qc.invalidateQueries({ queryKey: ['batch', batch.id] });

  return (
    <Modal onClose={onClose} size="lg">
      <header className="flex items-center justify-between border-b border-border bg-surface-raised px-4 py-3">
        <div>
          <h2 className="text-small font-semibold text-ink">
            Reopen &amp; fix — week of {batch.weekMonday}
          </h2>
          <p className="mt-0.5 text-caption text-ink-muted">
            {step === 'reopen' && 'Step 1 of 3 · Take the week back out of the ledgers'}
            {step === 'fix' && 'Step 2 of 3 · Point the wrong row(s) at the right account'}
            {step === 'repost' && 'Step 3 of 3 · Put the corrected week back'}
            {step === 'done' && 'All done'}
          </p>
        </div>
        <Button variant="ghost" onClick={onClose}>Close</Button>
      </header>

      <div className="flex-1 overflow-y-auto p-4">
        {error && <div className="mb-3"><ErrorBanner>{error}</ErrorBanner></div>}
        {step === 'reopen' && (
          <ReopenStep
            batchId={batch.id}
            onReopened={() => {
              setError(null);
              invalidateBatch();
              setStep('fix');
            }}
            onError={setError}
            withPin={withPin}
          />
        )}
        {step === 'fix' && (
          <FixStep
            batchId={batch.id}
            onContinue={() => {
              setError(null);
              setStep('repost');
            }}
            onError={setError}
            withPin={withPin}
          />
        )}
        {step === 'repost' && (
          <RepostStep
            batchId={batch.id}
            onReposted={(n) => {
              setError(null);
              setEntriesCreated(n);
              invalidateBatch();
              setStep('done');
            }}
            onError={setError}
          />
        )}
        {step === 'done' && (
          <div className="space-y-3 text-small text-ink">
            <p className="font-medium">
              Done — {entriesCreated ?? 0} corrected entries are back in the ledgers.
            </p>
            <p className="text-ink-muted">
              The old entries stay visible as reversals (turn on “Show reversed
              entries” on any ledger to see the audit trail). Balances now
              reflect the corrected week.
            </p>
            <Button onClick={onClose}>Close</Button>
          </div>
        )}
      </div>
    </Modal>
  );
}

// ── Step 1: preview + unpost ────────────────────────────────────────────────

function ReopenStep({
  batchId,
  onReopened,
  onError,
  withPin,
}: {
  batchId: number;
  onReopened: () => void;
  onError: (msg: string | null) => void;
  withPin: (action: () => Promise<unknown>) => Promise<void>;
}) {
  const [reason, setReason] = useState('');
  const impactQ = useQuery<PostingImpact>({
    queryKey: ['batch', batchId, 'posting-impact'],
    queryFn: () => api.get<PostingImpact>(`/api/batches/${batchId}/posting-impact`),
  });

  const unpostMut = useMutation({
    mutationFn: (r: string) =>
      api.post<{ entriesReversed: number }>(`/api/batches/${batchId}/unpost`, {
        reason: r,
      }),
    onSuccess: onReopened,
    onError: (e) => {
      if (e instanceof ApiError && e.status === 423) return; // PIN modal takes over
      onError(e instanceof ApiError ? e.message : String(e));
    },
  });

  const reopen = () => {
    const trimmed = reason.trim();
    if (trimmed.length === 0) {
      onError('Please say why you are reopening this week — it goes on the reversal entries.');
      return;
    }
    onError(null);
    void withPin(() => unpostMut.mutateAsync(trimmed));
  };

  if (impactQ.isLoading) {
    return (
      <div className="flex items-center gap-2 text-small text-ink-muted">
        <Spinner /> loading…
      </div>
    );
  }
  const impact = impactQ.data;
  return (
    <div className="space-y-4">
      <p className="text-small text-ink">
        This reverses the{' '}
        <strong>{impact?.entryCount ?? 0} ledger entries</strong> this week
        created, fixes can then be made, and reposting re-creates them with the
        corrected values. Nothing is deleted — the reversals stay on the books.
      </p>

      {impact && impact.parties.length > 0 && (
        <div>
          <h3 className="mb-2 text-small font-semibold text-ink">
            Balances while the week is reopened
          </h3>
          <ul className="space-y-2">
            {impact.parties.map((p) => (
              <li
                key={p.partyId}
                className="flex items-center justify-between gap-2 rounded-md border border-border bg-surface px-2.5 py-1.5 text-small"
              >
                <span className="min-w-0 truncate font-medium text-ink">{p.displayName}</span>
                <span className="shrink-0 font-mono text-ink">
                  {p.balanceBefore}
                  <span className="mx-1.5 text-ink-muted">→</span>
                  {p.balanceAfter}
                  <span className="ml-1 text-caption text-ink-muted">K-INR</span>
                </span>
              </li>
            ))}
          </ul>
          <p className="mt-1.5 text-caption text-ink-muted">
            Temporary — reposting in step 3 brings the corrected amounts back.
          </p>
        </div>
      )}

      <TextArea
        label="Why is this week being reopened?"
        rows={2}
        value={reason}
        onChange={(e) => setReason(e.target.value)}
        placeholder="e.g. Row for LASER247AURA was booked to the wrong account"
      />

      <Button variant="danger" onClick={reopen} disabled={unpostMut.isPending}>
        {unpostMut.isPending ? <Spinner /> : 'Reopen this week'}
      </Button>
    </div>
  );
}

// ── Step 2: reclassify raw rows ─────────────────────────────────────────────

function FixStep({
  batchId,
  onContinue,
  onError,
  withPin,
}: {
  batchId: number;
  onContinue: () => void;
  onError: (msg: string | null) => void;
  withPin: (action: () => Promise<unknown>) => Promise<void>;
}) {
  const qc = useQueryClient();
  const rawRowsQ = useQuery<RawUplineRow[]>({
    queryKey: ['batch', batchId, 'raw-rows'],
    queryFn: () => api.get<RawUplineRow[]>(`/api/batches/${batchId}/raw-rows`),
  });
  const accountsQ = useQuery<Account[]>({
    queryKey: ['accounts'],
    queryFn: () => api.get<Account[]>('/api/accounts'),
  });

  const accountOptions = useMemo(
    () =>
      (accountsQ.data ?? []).map((a) => ({
        value: String(a.id),
        label: `${a.canonicalName}${a.panel ? ` · ${a.panel}` : ''}`,
      })),
    [accountsQ.data],
  );

  if (rawRowsQ.isLoading || accountsQ.isLoading) {
    return (
      <div className="flex items-center gap-2 text-small text-ink-muted">
        <Spinner /> loading…
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <p className="text-small text-ink-muted">
        Pick the correct account for any row that was booked wrongly, then
        Apply. Amounts are recalculated immediately. Wrong points or rate
        instead? Fix those on the batch page (Edit on the settlement row),
        then come back here and continue.
      </p>
      <ul className="space-y-2">
        {(rawRowsQ.data ?? []).map((row) => (
          <FixRow
            key={row.id}
            row={row}
            accountOptions={accountOptions}
            onError={onError}
            withPin={withPin}
            onApplied={() =>
              void qc.invalidateQueries({ queryKey: ['batch', batchId] })
            }
          />
        ))}
        {(rawRowsQ.data ?? []).length === 0 && (
          <li className="text-small text-ink-muted">No raw rows in this batch.</li>
        )}
      </ul>
      <Button onClick={onContinue}>Continue to repost</Button>
    </div>
  );
}

function FixRow({
  row,
  accountOptions,
  onError,
  withPin,
  onApplied,
}: {
  row: RawUplineRow;
  accountOptions: { value: string; label: string }[];
  onError: (msg: string | null) => void;
  withPin: (action: () => Promise<unknown>) => Promise<void>;
  onApplied: () => void;
}) {
  const [accountId, setAccountId] = useState(
    row.matchedAccountId !== null ? String(row.matchedAccountId) : '',
  );
  const changed =
    accountId !== (row.matchedAccountId !== null ? String(row.matchedAccountId) : '');

  const patchMut = useMutation({
    mutationFn: () =>
      api.patch<RawRowReclassifyResult>(`/api/raw-rows/${row.id}/match`, {
        accountId: accountId === '' ? null : Number(accountId),
      }),
    onSuccess: () => {
      onError(null);
      onApplied();
    },
    onError: (e) => {
      if (e instanceof ApiError && e.status === 423) return;
      onError(e instanceof ApiError ? e.message : String(e));
    },
  });

  return (
    <li className="rounded-md border border-border bg-surface p-3">
      <div className="flex flex-wrap items-end justify-between gap-2">
        <div className="min-w-0 text-small">
          <span className="font-medium text-ink">{row.rawAccountName}</span>
          <span className="ml-2 text-caption text-ink-muted">
            {row.pointsStated} × {row.rateStated} = {row.amountStated}
          </span>
        </div>
        <div className="flex items-end gap-2">
          <Select
            label="Account"
            value={accountId}
            onChange={setAccountId}
            options={accountOptions}
            placeholder="— not matched —"
          />
          <Button
            size="sm"
            disabled={!changed || patchMut.isPending}
            onClick={() => void withPin(() => patchMut.mutateAsync())}
          >
            {patchMut.isPending ? <Spinner /> : 'Apply'}
          </Button>
        </div>
      </div>
    </li>
  );
}

// ── Step 3: repost ──────────────────────────────────────────────────────────

function RepostStep({
  batchId,
  onReposted,
  onError,
}: {
  batchId: number;
  onReposted: (entriesCreated: number) => void;
  onError: (msg: string | null) => void;
}) {
  const blockersQ = useQuery<VerifyBlocker[]>({
    queryKey: ['batch', batchId, 'blockers'],
    queryFn: () => api.get<VerifyBlocker[]>(`/api/batches/${batchId}/blockers`),
  });
  const totalsQ = useQuery<BatchTotals>({
    queryKey: ['batch', batchId, 'totals'],
    queryFn: () => api.get<BatchTotals>(`/api/batches/${batchId}/totals`),
  });
  const batchQ = useQuery<WeeklyBatch>({
    queryKey: ['batch', batchId],
    queryFn: () => api.get<WeeklyBatch>(`/api/batches/${batchId}`),
  });

  const verifyMut = useMutation({
    mutationFn: () => api.post<WeeklyBatch>(`/api/batches/${batchId}/verify`),
    onError: (e) => onError(e instanceof ApiError ? e.message : String(e)),
  });

  const postMut = useMutation({
    mutationFn: async () => {
      // The batch is normally still 'verified' after an unpost; but if a fix
      // in step 2 required unlocking to draft, verify again first.
      if (batchQ.data?.state === 'draft') {
        await verifyMut.mutateAsync();
      }
      return api.post<{ entriesCreated: number }>(`/api/batches/${batchId}/post`);
    },
    onSuccess: (r) => onReposted(r.entriesCreated),
    onError: (e) => onError(e instanceof ApiError ? e.message : String(e)),
  });

  if (blockersQ.isLoading || totalsQ.isLoading) {
    return (
      <div className="flex items-center gap-2 text-small text-ink-muted">
        <Spinner /> loading…
      </div>
    );
  }

  const { blocks } = splitBySeverity(blockersQ.data ?? []);

  return (
    <div className="space-y-4">
      {blocks.length > 0 ? (
        <div className="rounded-md border border-neg/30 bg-neg/5 p-3 text-small">
          <p className="font-semibold text-neg">
            {blocks.length} thing(s) must be sorted out before reposting:
          </p>
          <ul className="mt-1 list-disc pl-5 text-ink">
            {blocks.map((b, i) => (
              <li key={i}>{b.detail}</li>
            ))}
          </ul>
          <p className="mt-2 text-caption text-ink-muted">
            Fix these on the batch page, then reopen this wizard to repost.
          </p>
        </div>
      ) : (
        <p className="text-small text-ink">
          Everything checks out. Reposting writes{' '}
          <strong>{totalsQ.data?.rowCount ?? 0} settlement(s)</strong> back into
          the ledgers with the corrected values
          {totalsQ.data ? (
            <>
              {' '}
              (your margin this week:{' '}
              <span className="font-mono">{totalsQ.data.userMarginSum}</span>).
            </>
          ) : (
            '.'
          )}
        </p>
      )}

      <Button
        onClick={() => postMut.mutate()}
        disabled={blocks.length > 0 || postMut.isPending}
      >
        {postMut.isPending ? <Spinner /> : 'Repost to ledgers'}
      </Button>
    </div>
  );
}
